import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormArray,
  FormBuilder,
  FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';
import { ReportService } from '../../../services/native-report.service';

@Component({
    selector: 'app-edit-native-soil-report',
    templateUrl: './edit-native-soil-report.component.html',
    styleUrls: ['./edit-native-soil-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditNativeSoilReportComponent implements OnInit {
  form!: FormGroup;
  today = new Date();

  previewData: any = null;
  @Input() projectId = '';
  @Input() reportId = '';
  @Input() fieldtesting = false;

  @ViewChild('attachmentFileInput') attachmentFileInput!: ElementRef<HTMLInputElement>;

  allowedAttachmentFormat = 'application/pdf, image/jpeg, image/png, image/jpg';

  selectedAttachments: string[] = [];

  checkedAttachments = new Set<string>();

  attachmentsSaving = false;

  previewDisabled = false;
  previewPdfDisabled = false;
  showPoints = true;
  metricUnitValue = false;
  originalThermalData: any[] = [];

  // Free-form editable table shown before the thermal-data table. The first row is
  // the header row. Column widths / row heights (in px) drive drag-to-resize.
  customTable: { columnWidths: number[]; rowHeights: number[]; rows: string[][] } = {
    columnWidths: [],
    rowHeights: [],
    rows: [],
  };

  private resizeState: { type: 'column' | 'row'; index: number; startPos: number; startSize: number } | null = null;

  // Column being dragged, and the column its pointer is currently over (drives the
  // drop indicator). Both null when no column drag is in progress.
  draggedColumn: number | null = null;
  dragOverColumn: number | null = null;

  private static readonly DEFAULT_SIGNATURE_ID = 'C063AD0D-903F-447B-BCCC-A3C014CFAD7E';
  private static readonly DEFAULT_COLUMN_WIDTH = 120;
  private static readonly DEFAULT_ROW_HEIGHT = 40;
  private static readonly MIN_COLUMN_WIDTH = 40;
  private static readonly MIN_ROW_HEIGHT = 24;

  constructor(private fb: FormBuilder, private reportService: ReportService, private router: Router) {}

  ngOnInit(): void {
    this.initForm();

    this.form.get('isMetricUnit')!.valueChanges.subscribe((val: string) => {
      const newIsMetric = val === 'Metric';
      if (newIsMetric !== this.metricUnitValue) {
        this.convertThermalDataValues(this.metricUnitValue, newIsMetric);
        this.metricUnitValue = newIsMetric;
      }
    });

    if (this.projectId) {
      this.reportService.fetchReportData(this.projectId).subscribe({
        next: (data) => {
          this.previewData = data;
          this.patchForm(data);
        },
      });
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      reportDate: [null],
      isBilling: [false],
      mainBodyIntro: [''],
      mainBody: [''],
      thermalData: this.fb.array([]),
      commentPart1: [''],
      commentPart2: [''],
      commentPart3: [''],
      signatureTypeId: [EditNativeSoilReportComponent.DEFAULT_SIGNATURE_ID],
      isMetricUnit: ['Imperial'],
      companyName: [''],
      clientCompanyName: [''],
    });
  }

  private patchForm(data: any): void {
    const isMetric = !!data.isMetricUnit;
    this.metricUnitValue = isMetric;
    this.showPoints = !!data.showPoints;
    this.originalThermalData = (data.thermalData ?? []).map((r: any) => ({
      ...r,
    }));

    this.form.patchValue({
      reportDate: data.reportDate ? new Date(DateTimeUtils.getLocalDateTimeFromUTCDate(data.reportDate)) : null,
      isBilling: data.isBilling,
      mainBodyIntro: this.coerceString(data.mainBodyIntro),
      mainBody: this.coerceString(data.mainBody),
      commentPart1: this.coerceString(data.commentPart1),
      commentPart2: this.coerceString(data.commentPart2),
      commentPart3: this.coerceString(data.commentPart3),
      signatureTypeId: data.signatureTypeId ?? EditNativeSoilReportComponent.DEFAULT_SIGNATURE_ID,
      isMetricUnit: isMetric ? 'Metric' : 'Imperial',
      companyName: this.coerceString(data.companyName),
      clientCompanyName: data.clientCompanyName,
    }, {
      emitEvent: false,
    });

    this.populateThermalArray(data.thermalData, isMetric);
    this.parseFieldTestResults(data.fieldTestResults);
    this.selectedAttachments = data.attachments;
  }

  // Restore the custom (field test results) table from the JSON string saved by
  // serializeFieldTestResults. Falls back to the default table if it's missing or
  // malformed, and backfills sizes so the arrays always match the row/column count.
  private parseFieldTestResults(value: any): void {
    if (!value) return;
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      const rows: string[][] = Array.isArray(parsed?.rows) ? parsed.rows : [];
      if (!rows.length) return;

      const defaultWidth = EditNativeSoilReportComponent.DEFAULT_COLUMN_WIDTH;
      const defaultHeight = EditNativeSoilReportComponent.DEFAULT_ROW_HEIGHT;
      const parsedWidths: number[] = Array.isArray(parsed.columnWidths) ? parsed.columnWidths : [];
      const parsedHeights: number[] = Array.isArray(parsed.rowHeights) ? parsed.rowHeights : [];

      this.customTable = {
        rows: rows.map((row) => row.slice()),
        columnWidths: rows[0].map((_, c) => parsedWidths[c] ?? defaultWidth),
        rowHeights: rows.map((_, r) => parsedHeights[r] ?? defaultHeight),
      };
    } catch {
      // Keep the default table on invalid JSON.
    }
  }

  get customTableWidth(): number {
    return this.customTable.columnWidths.reduce((sum, width) => sum + width, 0);
  }

  // Any edit to the field test results table (cell text, add/delete, or resize)
  // must mark the form dirty since the table isn't a reactive form control.
  markFieldTestResultsDirty(): void {
    this.form.markAsDirty();
  }

  addCustomColumn(): void {
    this.customTable.columnWidths.push(EditNativeSoilReportComponent.DEFAULT_COLUMN_WIDTH);
    this.customTable.rows.forEach((row) => row.push(''));
    this.markFieldTestResultsDirty();
  }

  addCustomRow(): void {
    this.customTable.rows.push(Array(this.customTable.columnWidths.length).fill(''));
    this.customTable.rowHeights.push(EditNativeSoilReportComponent.DEFAULT_ROW_HEIGHT);
    this.markFieldTestResultsDirty();
  }

  deleteCustomColumn(index: number): void {
    // The first column holds the header values, so it can never be removed.
    if (index <= 0 || index >= this.customTable.columnWidths.length) return;
    this.customTable.columnWidths.splice(index, 1);
    this.customTable.rows.forEach((row) => row.splice(index, 1));
    this.markFieldTestResultsDirty();
  }

  deleteCustomRow(index: number): void {
    // The first row holds the header values, so it can never be removed.
    if (index <= 0 || index >= this.customTable.rows.length) return;
    this.customTable.rows.splice(index, 1);
    this.customTable.rowHeights.splice(index, 1);
    this.markFieldTestResultsDirty();
  }

  onColumnDragStart(event: DragEvent, index: number): void {
    if (index === 0) {
      event.preventDefault();
      return;
    }
    this.draggedColumn = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      // Firefox only starts a drag once some payload is set.
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  onColumnDragOver(event: DragEvent, index: number): void {
    if (this.draggedColumn === null || index === 0) return;
    // Without preventDefault the browser refuses the drop.
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    this.dragOverColumn = index;
  }

  onColumnDragLeave(index: number): void {
    if (this.dragOverColumn === index) this.dragOverColumn = null;
  }

  onColumnDrop(event: DragEvent, index: number): void {
    event.preventDefault();
    const from = this.draggedColumn;
    this.onColumnDragEnd();
    if (from === null) return;
    this.moveCustomColumn(from, index);
  }

  onColumnDragEnd(): void {
    this.draggedColumn = null;
    this.dragOverColumn = null;
  }

  // Move a column, carrying its width and every row's cell along with it so the
  // three parallel arrays stay aligned.
  private moveCustomColumn(from: number, to: number): void {
    const lastIndex = this.customTable.columnWidths.length - 1;
    if (from === to || from < 0 || to < 0 || from > lastIndex || to > lastIndex) return;
    if (from === 0 || to === 0) return;
    moveItemInArray(this.customTable.columnWidths, from, to);
    this.customTable.rows.forEach((row) => moveItemInArray(row, from, to));
    this.markFieldTestResultsDirty();
  }

  startColumnResize(event: MouseEvent, index: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizeState = {
      type: 'column',
      index,
      startPos: event.clientX,
      startSize: this.customTable.columnWidths[index],
    };
  }

  startRowResize(event: MouseEvent, index: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizeState = {
      type: 'row',
      index,
      startPos: event.clientY,
      startSize: this.customTable.rowHeights[index],
    };
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.resizeState) return;
    if (this.resizeState.type === 'column') {
      const delta = event.clientX - this.resizeState.startPos;
      this.customTable.columnWidths[this.resizeState.index] = Math.max(
        EditNativeSoilReportComponent.MIN_COLUMN_WIDTH,
        this.resizeState.startSize + delta,
      );
    } else {
      const delta = event.clientY - this.resizeState.startPos;
      this.customTable.rowHeights[this.resizeState.index] = Math.max(
        EditNativeSoilReportComponent.MIN_ROW_HEIGHT,
        this.resizeState.startSize + delta,
      );
    }
    this.markFieldTestResultsDirty();
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    this.resizeState = null;
  }

  private populateThermalArray(rows: any[] | null | undefined, isMetric: boolean): void {
    const thermalArray = this.form.get('thermalData') as FormArray;
    thermalArray.clear();
    (rows ?? []).forEach((r: any) => thermalArray.push(this.buildThermalRow(r, isMetric)));
  }

  private buildThermalRow(r: any, isMetric: boolean): FormGroup {
    const isStockPile = !!r.isStockPile;
    return this.fb.group({
      sampleId: [r.sampleId],
      startDepth: [this.depthForDisplay(r.depth, isStockPile, isMetric)],
      endDepth: [this.depthForDisplay(r.endDepth, isStockPile, isMetric)],
      effort: [r.effort],
      description: [r.description],
      wet: [this.displayTr(r.wet, isMetric)],
      dry: [this.displayTr(r.dry, isMetric)],
      moistureContent: [r.moistureContent],
      density: [isMetric ? this.convertDensity(r.density, true) : r.density],
      nativeSoilSampleID: [r.nativeSoilSampleID],
      isStockPile: [isStockPile],
      groupID: [r.groupID],
    });
  }

  private depthForDisplay(value: any, isStockPile: boolean, isMetric: boolean): any {
    if (isStockPile) return 'Stockpile';
    return isMetric ? this.convertDepth(value, true) : value;
  }

  private depthForSave(value: any, isStockPile: boolean, original: any): any {
    if (isStockPile) return original;
    const normalized = this.normalizeDepth(value);
    if (normalized === null) return null;
    return this.metricUnitValue ? this.convertDepth(normalized, false) : normalized;
  }

  private normalizeDepth(value: any): any {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    return value;
  }

  private displayTr(value: any, isMetric: boolean): any {
    if (value === null || value === undefined || value === '') return value;
    if (isMetric) {
      const converted = this.convertThermalResistivity(value, true);
      const num = Number(converted);
      if (Number.isNaN(num)) return converted;
      return Math.round((num + Number.EPSILON) * 100) / 100;
    }
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    if (num <= 10) return Math.round((num + Number.EPSILON) * 10) / 10;
    return Math.round(num);
  }

  private coerceString(value: any): string {
    return value == null || value === '' ? '' : value;
  }

  get thermalDataArray(): FormArray {
    return this.form.get('thermalData') as FormArray;
  }

  compareSignatureIds = (a: string | null | undefined, b: string | null | undefined): boolean => {
    if (a == null || b == null) return a === b;
    return String(a).toLowerCase() === String(b).toLowerCase();
  };

  preview(): void {
    const formValue = this.form.value;
    const thermalData = (formValue.thermalData ?? []).map((row: any) => {
      const { startDepth, endDepth, ...rest } = row;
      const isStockPile = !!rest.isStockPile;
      return {
        ...rest,
        depth: isStockPile ? 0 : this.normalizeDepth(startDepth),
        endDepth: isStockPile ? 0 : this.normalizeDepth(endDepth),
      };
    });

    const payload = this.buildPayload(formValue, thermalData);
    this.previewDisabled = true;
    this.reportService.createNativeSoilReportFromData(payload).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(blob, `${this.buildReportName(payload)}.docx`);
        this.previewDisabled = false;
      },
      error: () => {
        this.previewDisabled = false;
      },
    });
  }

  previewPdf(): void {
    const formValue = this.form.value;
    const thermalData = (formValue.thermalData ?? []).map((row: any) => {
      const { startDepth, endDepth, ...rest } = row;
      const isStockPile = !!rest.isStockPile;
      return {
        ...rest,
        depth: isStockPile ? 0 : this.normalizeDepth(startDepth),
        endDepth: isStockPile ? 0 : this.normalizeDepth(endDepth),
      };
    });

    const payload = this.buildPayload(formValue, thermalData);
    this.previewPdfDisabled = true;
    this.reportService.createNativeSoilReportPDFFromData(payload).subscribe({
      next: (blob: Blob) => {
        this.openBlobInNewTab(blob, `${this.buildReportName(payload)}.pdf`);
        this.previewPdfDisabled = false;
      },
      error: () => {
        this.previewPdfDisabled = false;
      },
    });
  }

  save(): void {
    const formValue = this.form.value;
    const formThermalData = formValue.thermalData ?? [];
    const thermalData = this.originalThermalData.map((row: any, i: number) => {
      const formRow = formThermalData[i];
      if (!formRow) {
        return {
          ...row,
        };
      }
      const isStockPile = !!row.isStockPile;
      return {
        ...row,
        sampleId: formRow.sampleId,
        depth: this.depthForSave(formRow.startDepth, isStockPile, row.depth),
        endDepth: this.depthForSave(formRow.endDepth, isStockPile, row.endDepth),
        description: formRow.description,
      };
    });

    this.reportService.updateReport(this.buildPayload(formValue, thermalData)).subscribe({
      next: () => {
        this.form.markAsPristine();
      },
    });
  }

  private buildPayload(formValue: any, thermalData: any[]): any {
    const reportDate = new Date(formValue.reportDate);
    const now = new Date();
    reportDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    return {
      ...this.previewData,
      reportDate,
      reportDateString: this.formatDateOnly(reportDate),
      isBilling: formValue.isBilling,
      mainBodyIntro: formValue.mainBodyIntro,
      mainBody: formValue.mainBody,
      commentPart1: formValue.commentPart1,
      commentPart2: formValue.commentPart2,
      commentPart3: formValue.commentPart3,
      signatureTypeId: formValue.signatureTypeId,
      isMetricUnit: formValue.isMetricUnit === 'Metric',
      showPoints: this.showPoints,
      companyName: formValue.companyName,
      thermalData,
      clientCompanyName: formValue.clientCompanyName,
      fieldTestResults: this.serializeFieldTestResults(),
    };
  }

  // Serialize the custom (field test results) table — its cell contents plus
  // column widths and row heights — as a JSON string for the API.
  private serializeFieldTestResults(): string {
    return JSON.stringify({
      rows: this.customTable.rows,
      columnWidths: this.customTable.columnWidths,
      rowHeights: this.customTable.rowHeights,
    });
  }

  private formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private downloadBlob(blob: Blob, filename?: string): void {
    const url = window.URL.createObjectURL(blob);
    const hyperlink = document.createElement('a');
    hyperlink.href = url;
    if (filename) {
      hyperlink.download = filename;
    }
    hyperlink.click();
    window.URL.revokeObjectURL(url);
  }

  private openBlobInNewTab(blob: Blob, filename: string): void {
    const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], {
      type: 'application/pdf',
    });
    const pdfUrl = window.URL.createObjectURL(pdfBlob);
    // A blob opened directly in a tab downloads as its GUID object-URL id, so the
    // PDF is wrapped in a same-origin page whose title and Download link both use
    // the report name, matching the Word download's filename.
    const pageUrl = window.URL.createObjectURL(
      new Blob([this.buildPdfViewerPage(pdfUrl, filename)], {
        type: 'text/html',
      }),
    );
    const viewer = window.open(pageUrl, '_blank');
    if (!viewer) {
      // Popup blocked: fall back to a direct named download.
      this.downloadBlob(pdfBlob, filename);
      window.URL.revokeObjectURL(pdfUrl);
      window.URL.revokeObjectURL(pageUrl);
      return;
    }
    viewer.addEventListener('beforeunload', () => {
      window.URL.revokeObjectURL(pdfUrl);
      window.URL.revokeObjectURL(pageUrl);
    });
  }

  private buildPdfViewerPage(pdfUrl: string, filename: string): string {
    const safeName = this.escapeHtml(filename);
    return `<!doctype html><html><head><meta charset="utf-8"><title>${safeName}</title>`
      + '<style>html,body{margin:0;height:100%;font-family:Arial,sans-serif}'
      + '#bar{height:40px;display:flex;align-items:center;justify-content:flex-end;'
      + 'background:#f1f1f1;padding:0 12px;box-sizing:border-box}'
      + '#bar a{background:#3f51b5;color:#fff;text-decoration:none;padding:6px 14px;'
      + 'border-radius:4px;font-size:14px}'
      + 'iframe{border:0;width:100%;height:calc(100% - 40px);display:block}</style></head>'
      + `<body><div id="bar"><a download="${safeName}" href="${pdfUrl}">Download</a></div>`
      + `<iframe src="${pdfUrl}" type="application/pdf"></iframe></body></html>`;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildReportName(payload: any): string {
    const parts = [
      this.sanitizeNamePart(payload?.clientCompanyName),
      this.sanitizeNamePart(payload?.projectName),
      this.sanitizeNamePart(payload?.projectNumber),
      'NS',
      this.formatReportDateForName(payload?.reportDate),
    ];
    return parts.filter((p) => p && p.length > 0).join('-');
  }

  private sanitizeNamePart(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[\\/:*?"<>|]/g, '').trim();
  }

  private formatReportDateForName(value: any): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear() % 100).padStart(2, '0');
    return `${mm}${dd}${yy}`;
  }

  AddAttachments(): void {
    this.attachmentFileInput.nativeElement.click();
  }

  onAttachmentsSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const picked = Array.from(input.files ?? []).filter((file) => this.isAllowedAttachment(file));
    input.value = '';

    const reportId = this.reportId || this.previewData?.id;
    if (!picked.length || !reportId || this.attachmentsSaving) return;

    this.attachmentsSaving = true;
    this.reportService.addReportAttachments(reportId, picked).subscribe({
      next: () => {
        this.selectedAttachments = [...new Set([...this.selectedAttachments, ...picked.map((file) => file.name)])];
        this.attachmentsSaving = false;
      },
      error: () => {
        this.attachmentsSaving = false;
      },
    });
  }

  dropAttachment(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.selectedAttachments, event.previousIndex, event.currentIndex);

    const reportId = this.reportId || this.previewData?.id;
    if (!reportId) return;
    this.reportService.updateAttachmentPositions(reportId, this.selectedAttachments).subscribe();
  }

  get allAttachmentsChecked(): boolean {
    return this.selectedAttachments.length > 0 && this.selectedAttachments.every((file) => this.checkedAttachments.has(file));
  }

  get someAttachmentsChecked(): boolean {
    return this.checkedAttachments.size > 0 && !this.allAttachmentsChecked;
  }

  toggleAllAttachments(checked: boolean): void {
    this.checkedAttachments = checked ? new Set(this.selectedAttachments) : new Set<string>();
  }

  toggleAttachmentChecked(file: string, checked: boolean): void {
    if (checked) {
      this.checkedAttachments.add(file);
    } else {
      this.checkedAttachments.delete(file);
    }
  }

  removeAttachment(index: number): void {
    this.deleteAttachments([this.selectedAttachments[index]]);
  }

  deleteSelectedAttachments(): void {
    this.deleteAttachments(this.selectedAttachments.filter((file) => this.checkedAttachments.has(file)));
  }

  private deleteAttachments(files: string[]): void {
    const reportId = this.reportId || this.previewData?.id;
    if (!files.length || !reportId || this.attachmentsSaving) return;

    this.attachmentsSaving = true;
    this.reportService.deleteAttachments(reportId, files).subscribe({
      next: () => {
        this.selectedAttachments = this.selectedAttachments.filter((file) => !files.includes(file));
        files.forEach((file) => this.checkedAttachments.delete(file));
        this.attachmentsSaving = false;
      },
      error: () => {
        this.attachmentsSaving = false;
      },
    });
  }

  formatSize(size: number): string {
    if (size >= 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }
    if (size >= 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${size} byte`;
  }

  private isAllowedAttachment(file: File): boolean {
    const type = (file.type ?? '').toLowerCase();
    if (type) {
      return this.allowedAttachmentFormat
        .split(',')
        .map((format) => format.trim().toLowerCase())
        .includes(type);
    }
    return /\.(pdf|png|jpe?g)$/i.test(file.name);
  }

  editFigures(): void {
    this.router.navigate(['/report/thermal', 'nativesoil', this.projectId]);
  }

  formatDate(iso: string): string {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private convertThermalDataValues(fromMetric: boolean, toMetric: boolean): void {
    if (fromMetric === toMetric) return;

    this.thermalDataArray.controls.forEach((ctrl) => {
      const group = ctrl as FormGroup;
      const isStockPile = !!group.get('isStockPile')?.value;

      const startDepthCtrl = group.get('startDepth');
      if (startDepthCtrl && !isStockPile) {
        startDepthCtrl.setValue(this.convertDepth(startDepthCtrl.value, toMetric), {
          emitEvent: false,
        });
      }

      const endDepthCtrl = group.get('endDepth');
      if (endDepthCtrl && !isStockPile) {
        endDepthCtrl.setValue(this.convertDepth(endDepthCtrl.value, toMetric), {
          emitEvent: false,
        });
      }

      const wetCtrl = group.get('wet');
      if (wetCtrl) {
        wetCtrl.setValue(this.convertThermalResistivity(wetCtrl.value, toMetric), {
          emitEvent: false,
        });
      }

      const dryCtrl = group.get('dry');
      if (dryCtrl) {
        dryCtrl.setValue(this.convertThermalResistivity(dryCtrl.value, toMetric), {
          emitEvent: false,
        });
      }

      const densityCtrl = group.get('density');
      if (densityCtrl) {
        densityCtrl.setValue(this.convertDensity(densityCtrl.value, toMetric), {
          emitEvent: false,
        });
      }
    });
  }

  private convertDepth(value: any, toMetric: boolean): any {
    if (value === null || value === undefined) return value;
    if (typeof (value) === 'string' && value.trim() === '') {
      return null;
    }
    const num = Number(value);
    if (!Number.isNaN(num)) {
      return this.convertDepthNumber(num, toMetric);
    }
    return value;
  }

  private convertDepthNumber(value: number, toMetric: boolean): number {
    const converted = toMetric ? value * 0.3048 : value / 0.3048;
    return converted;
  }

  private convertThermalResistivity(value: any, toMetric: boolean): any {
    if (value === null || value === undefined || value === '') return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    const converted = toMetric ? num / 100 : num * 100;
    return converted;
  }

  private convertDensity(value: any, toMetric: boolean): any {
    if (value === null || value === undefined || value === '') return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    const converted = toMetric ? num * 16.0185 : num / 16.0185;
    return converted;
  }
}
